import { Button, Table, PageHeader, Modal, Col, Select, Input } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Controller, useForm } from "react-hook-form";
import FormLayout, { FormItem } from "Components/form";
import DatePicker from "Components/date-picker-localized";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { createComponentStore, useStoreState } from "easy-peasy";
import { clean, sort } from "Utils";

import { HOUSING_SCORING_POINT_TYPE } from "Constants";

import {
  HousingScoringStoreModel,
  ACTION_TYPES,
} from "store/models/master-data/housing-scoring";
import ExportExcel from "Components/export-excel";
import FetchSelect from "Components/fetch-select";
import PartnerSelect from "Components/partner-select";
import { AuthCheck, AUTH_ACTIONS } from "authorization";
import FourOThree from "Pages/private/403";

const useHosingScoringStore = createComponentStore(HousingScoringStoreModel);

export default function HouseScoringReportPage() {
  const [store, actions] = useHosingScoringStore();
  const { fetch, searchHelp } = actions;
  const [filters, setFilters] = useState();
  const { process, data } = store;
  const [filterModalVisible, setFilterModalVisible] = useState(false);

  const { abilities } = useStoreState((store) => store.auth);
  const IsAuthorized = AuthCheck(
    abilities,
    AUTH_ACTIONS.READ,
    "Konut Puanlama Raporları"
  );

  useEffect(() => {
    searchHelp({ state: "ACTIVE", ...filters });
  }, [filters]);

  const form = useForm({
    mode: "onSubmit",
    defaultValues: {
      taskdistilecodes: null,
      positions: null,
      dateOfPeriod: null,
    },
  });

  const { handleSubmit, reset } = form;

  const handleAdvancedSearchOK = (formD) => {
    let filterData = { ...formD };
    delete filterData.createdAt;
    if (formD.createdAt?.length > 0) {
      filterData.createdAtBegin = moment(formD.createdAt[0]).format(
        "yyyy-MM-DDT00:00:00"
      );
      filterData.createdAtAfter = moment(formD.createdAt[1]).format(
        "yyyy-MM-DDT00:00:00"
      );
    }

    clean(filterData);
    setFilters(filterData);
    setFilterModalVisible(false);
  };

  const handleAdvancedSearchCancel = () => {
    reset();
    setFilters({});
    setFilterModalVisible(false);
  };

  const columns = [
    {
      title: "Kod",
      dataIndex: "code",
      key: "code",
      width: 80,
      ellipsis: true,
      sorter: (a, b) => a.code.substr(3) - b.code.substr(3),
    },
    {
      title: "Muhatap",
      dataIndex: "partner",
      key: "partner",
      width: 170,
      ellipsis: true,
      sorter: (a, b) =>
        sort(
          (a.partner?.name || "") + " " + (a.partner?.surname || ""),
          (b.partner?.name || "") + " " + (b.partner?.surname || "")
        ),
      render: (text, record) =>
        (record.partner?.name || "") + " " + (record.partner?.surname || ""),
    },

    {
      title: "Görev",
      dataIndex: ["partner", "partnerTask", "description"],
      key: "task",
      width: 170,
      ellipsis: true,
      sorter: (a, b) =>
        sort(
          a.partner?.partnerTask?.description,
          b.partner?.partnerTask?.description
        ),
    },

    {
      title: "Görev Sırası",
      dataIndex: ["partner", "partnerTask", "taskOrder"],
      key: "task",
      width: 120,
      ellipsis: true,
      sorter: (a, b) =>
        sort(
          a.partner?.partnerTask?.taskOrder,
          b.partner?.partnerTask?.taskOrder
        ),
    },

    {
      title: "Görev Ayrım Unvanı",
      dataIndex: ["partner", "taskDistinctionTitle", "description"],
      width: 170,
      ellipsis: true,
      sorter: (a, b) =>
        sort(
          a.partner?.taskDistinctionTitle?.description,
          b.partner?.taskDistinctionTitle?.description
        ),
    },

    {
      title: "Unvan Sırası",
      dataIndex: ["partner", "taskDistinctionTitle", "titleOrder"],
      width: 120,
      ellipsis: true,
      sorter: (a, b) =>
        sort(
          a.partner?.taskDistinctionTitle?.titleOrder,
          b.partner?.taskDistinctionTitle?.titleOrder
        ),
    },

    {
      title: "Puanlama Türü",
      dataIndex: "pointType",
      key: "pointType",
      width: 200,
      render: (text, record) => HOUSING_SCORING_POINT_TYPE[record.pointType],
      sorter: (a, b) =>
        sort(
          HOUSING_SCORING_POINT_TYPE[a.pointType],
          HOUSING_SCORING_POINT_TYPE[b.pointType]
        ),
      ellipsis: true,
    },
    {
      title: "Tahsise Esas Puan",
      dataIndex: "allocationBasicScore",
      key: "allocationBasicScore",
      width: 140,
      ellipsis: true,
      sorter: (a, b) => a.allocationBasicScore - b.allocationBasicScore,
    },

    {
      title: "Gelir Harici Puanı",
      dataIndex: "salaryExceptScore",
      key: "salaryExceptScore",
      width: 140,
      ellipsis: true,
      sorter: (a, b) => a.salaryExceptScore - b.salaryExceptScore,
    },

    {
      title: "Hizmet Süresi Puanı",
      dataIndex: "termOfOfficeScore",
      key: "termOfOfficeScore",
      width: 140,
      ellipsis: true,
      sorter: (a, b) => a.termOfOfficeScore - b.termOfOfficeScore,
    },

    {
      title: "Eş Durumu Puanı",
      dataIndex: "wifeScore",
      key: "wifeScore",
      width: 140,
      ellipsis: true,
      sorter: (a, b) => a.wifeScore - b.wifeScore,
    },

    {
      title: "Çocuk Durumu Puanı",
      dataIndex: "childrenScore",
      key: "childrenScore",
      width: 140,
      ellipsis: true,
      sorter: (a, b) => a.childrenScore - b.childrenScore,
    },

    {
      title: "Yükümlü Fertler Puanı",
      dataIndex: "obligatedOtherPerson",
      key: "obligatedOtherPerson",
      width: 140,
      ellipsis: true,
      sorter: (a, b) => a.obligatedOtherPerson - b.obligatedOtherPerson,
    },

    {
      title: "Konut Tahsisi Bekleme Puanı",
      dataIndex: "housingDemandWaitingScore",
      key: "housingDemandWaitingScore",
      width: 140,
      ellipsis: true,
      sorter: (a, b) =>
        a.housingDemandWaitingScore - b.housingDemandWaitingScore,
    },

    {
      title:
        "Yükümlü Fertlerin Belediye Mücavir Alan İçinde Konutu Olanlar Puanı",
      dataIndex: "housingMunicipalityContiguousInScore",
      key: "housingMunicipalityContiguousInScore",
      width: 140,
      ellipsis: true,
      sorter: (a, b) =>
        a.housingMunicipalityContiguousInScore -
        b.housingMunicipalityContiguousInScore,
    },

    {
      title:
        "Yükümlü Fertlerin Belediye Mücavir Alan Dışında Konutu Olanlar Puanı",
      dataIndex: "housingMunicipalityContiguousOutScore",
      key: "housingMunicipalityContiguousOutScore",
      width: 140,
      ellipsis: true,
      sorter: (a, b) =>
        a.housingMunicipalityContiguousOutScore -
        b.housingMunicipalityContiguousOutScore,
    },

    {
      title: "Engel Durumu Puanı",
      dataIndex: "disabilitySituationScore",
      key: "disabilitySituationScore",
      width: 140,
      ellipsis: true,
      sorter: (a, b) => a.disabilitySituationScore - b.disabilitySituationScore,
    },

    {
      title: "Yükümlü Fertlerin Engel Durumu Puanı",
      dataIndex: "disabilityFamilyMembersScore",
      key: "disabilityFamilyMembersScore",
      width: 140,
      ellipsis: true,
      sorter: (a, b) =>
        a.disabilityFamilyMembersScore - b.disabilityFamilyMembersScore,
    },

    {
      title: "Gazi veya Şehit Yakınlığı Puanı",
      dataIndex: "veteranScore",
      key: "veteranScore",
      width: 140,
      ellipsis: true,
      sorter: (a, b) => a.veteranScore - b.veteranScore,
    },

    {
      title: "Başvuru Tarihi",
      dataIndex: "dateOfApplication",
      key: "dateOfApplication",
      width: 110,
      ellipsis: true,
      render: (text, record) => {
        return moment(record.dateOfApplication).format("DD.MM.YYYY");
      },
      sorter: (a, b) => sort(a.dateOfApplication, b.dateOfApplication),
    },

    {
      title: "Oluşturma Tarihi",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 110,
      ellipsis: true,
      sorter: (a, b) => sort(a.createdAt, b.createdAt),
      render: (text, record) => {
        return moment(record.createdAt).format("DD.MM.YYYY");
      },
    },
  ];

  const getExcelColumns = () => {
    return columns.map((d) => d.title);
  };

  const getExcelRows = () => {
    let rows = [];
    store.data.forEach((d) => {
      let i = [];
      i.push(d.code || "-");
      i.push((d.partner?.name || "-") + " " + (d.partner?.surname || "-"));
      i.push(d.partner?.partnerTask?.description || "-");
      i.push(d.partner?.partnerTask?.taskOrder || "-");
      i.push(d.partner?.taskDistinctionTitle?.description || "-");
      i.push(d.partner?.taskDistinctionTitle?.titleOrder || "-");
      i.push(HOUSING_SCORING_POINT_TYPE[d.pointType] || "");
      i.push(d.allocationBasicScore || 0);
      i.push(d.salaryExceptScore || 0);
      i.push(d.termOfOfficeScore || 0);
      i.push(d.wifeScore || 0);
      i.push(d.childrenScore || 0);
      i.push(d.obligatedOtherPerson || 0);
      i.push(d.housingDemandWaitingScore || 0);
      i.push(d.housingMunicipalityContiguousInScore || 0);
      i.push(d.housingMunicipalityContiguousOutScore || 0);
      i.push(d.disabilitySituationScore || 0);
      i.push(d.disabilityFamilyMembersScore || 0);
      i.push(d.veteranScore || 0);
      i.push(moment(d.dateOfApplication).format("DD.MM.YYYY") || "-");
      i.push(moment(d.createdAt).format("DD.MM.YYYY") || "-");
      rows.push(i);
    });
    return rows;
  };

  return IsAuthorized ? (
    <>
      <PageHeader
        title="Konut Puanlama Raporları"
        extra={[
          <Button
            key="filter"
            onClick={() => {
              setFilterModalVisible(true);
            }}
          >
            <FontAwesomeIcon
              icon={["fas", "filter"]}
              style={{ marginRight: 8 }}
            />
            Detaylı Arama
          </Button>,

          <ExportExcel
            columnsData={getExcelColumns()}
            key={data}
            disabled={data.length < 1}
            rowsData={getExcelRows()}
            loading={store.process === ACTION_TYPES.FETCHING_DATA}
            filename={
              "Konut Puanlama Raporları_" +
              moment(new Date()).format("DD-MM-YYYY")
            }
          />,
        ]}
      />
      <Table
        style={{ margin: "0 24px" }}
        rowKey={(row) => row.id}
        loading={process === ACTION_TYPES.FETCHING_DATA}
        dataSource={data}
        columns={columns}
        size="small"
        bordered
        showSorterTooltip={false}
        scroll={{ x: 1000, scrollToFirstRowOnChange: true }}
        pagination={{
          pageSize: 20,
          total: data.length,
          showSizeChanger: false,
          showTotal: (total, range) => {
            return `Toplam ${total} sonuçtan ${range.join(
              "-"
            )} arası gösteriliyor.`;
          },
        }}
      />
      <Modal
        title="Detaylı Arama"
        centered
        maskClosable={false}
        visible={filterModalVisible}
        onOk={handleSubmit(handleAdvancedSearchOK)}
        onCancel={() => setFilterModalVisible(false)}
        footer={[
          <Button key="clear" onClick={handleAdvancedSearchCancel}>
            Temizle
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleSubmit(handleAdvancedSearchOK)}
          >
            <FontAwesomeIcon
              icon={["fas", "search"]}
              style={{ marginRight: 8 }}
            />
            Ara
          </Button>,
        ]}
        width="60vw"
      >
        <FormLayout>
          <Col xs={24} md={12}>
            <FormItem label="Kod">
              <Controller
                name="fastSearchParam"
                control={form.control}
                as={<Input placeholder="Kod" />}
              />
            </FormItem>
          </Col>
          <Col xs={24} md={12}>
            <FormItem label="Oluşturma Tarihi">
              <Controller
                name="createdAt"
                control={form.control}
                as={
                  <DatePicker
                    isRangePicker={true}
                    format="DD.MM.YYYY"
                    style={{ width: "100%" }}
                  />
                }
              />
            </FormItem>
          </Col>

          <Col xs={24} md={12}>
            <FormItem label="Muhatap">
              <PartnerSelect
                form={form}
                objectInValue={false}
                allowClear={true}
                field="partnerId"
                style={{ width: "100%" }}
              />
            </FormItem>
          </Col>
          <Col xs={24} md={12}>
            <FormItem htmlFor="taskId" label="Görev">
              <FetchSelect
                api="/masterdata/partnertasks"
                form={form}
                field="taskId"
                placeholder="Görev seçiniz"
                objectInValue={false}
              />
            </FormItem>
          </Col>

          <Col xs={24} md={12}>
            <FormItem label="Puanlama Türü">
              <Controller
                control={form.control}
                name="pointType"
                as={
                  <Select>
                    {Object.entries(HOUSING_SCORING_POINT_TYPE).map(
                      ([key, value]) => (
                        <Select.Option key={key} value={key}>
                          {value}
                        </Select.Option>
                      )
                    )}
                  </Select>
                }
              />
            </FormItem>
          </Col>
        </FormLayout>
      </Modal>
    </>
  ) : (
    <FourOThree />
  );
}
